import { Edge, Position } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import ELK from 'elkjs/lib/elk.bundled';
import type { LayoutNode } from '../../types';
import { nodeHeight, nodeWidth } from '../../constants';
import { layoutOptions } from './layout-options';
import { NODE_TYPES } from './node-types.constant';

const elk = new ELK();

// Ensure ELK always receives valid ids for nodes and edges - otherwise no nodes/edges will be displayed
const sanitizeForElk = (nodes: LayoutNode[], edges: Edge[]) => {
  const validNodes = nodes
    .filter((node) => node && node.id != null)
    .map((node) => ({ ...node, id: String(node.id) }));

  const validEdges = edges.filter(
    (edge) =>
      edge && edge.id != null && edge.source != null && edge.target != null,
  );

  return { validNodes, validEdges };
};

export const getLayoutedElements = async (
  nodes: LayoutNode[],
  edges: Edge[],
) => {
  if (nodes.length === 0) return { nodes, edges };

  const { validNodes, validEdges } = sanitizeForElk(nodes, edges);
  if (validNodes.length === 0) return { nodes, edges };

  // --- Pre-processing ordering hint ---------------------------------------------------------
  // ELK layered crossing minimization may reorder sibling nodes (targets in next layer)
  // which can cause the visual inversion of TRUE/FALSE branches. We attempt to preserve
  // semantic ordering by ensuring that for every decision node, the target of the `_true`
  // edge appears earlier in the model order than the `_false` target. With
  // 'elk.layered.considerModelOrder' and 'elk.layered.crossingMinimization.semiInteractive'
  // enabled, this acts as a soft constraint that *usually* eliminates the inversion without
  // heavy manual coordinate post-processing.
  const nodeIndexMap: Record<string, number> = {};
  validNodes.forEach((n, i) => {
    nodeIndexMap[n.id] = i;
  });
  const reorderedNodes = [...validNodes];
  const decisionEdgesBySource: Record<
    string,
    { true?: string; false?: string }
  > = {};
  validEdges.forEach((e) => {
    if (
      e.sourceHandle &&
      (e.sourceHandle.endsWith('_true') || e.sourceHandle.endsWith('_false'))
    ) {
      const result = e.sourceHandle.endsWith('_true') ? 'true' : 'false';
      decisionEdgesBySource[e.source] = decisionEdgesBySource[e.source] || {};
      decisionEdgesBySource[e.source][result] = e.target;
    }
  });
  Object.entries(decisionEdgesBySource).forEach(
    ([, { true: trueId, false: falseId }]) => {
      if (!trueId || !falseId) return;
      const trueIndex = reorderedNodes.findIndex((n) => n.id === trueId);
      const falseIndex = reorderedNodes.findIndex((n) => n.id === falseId);
      if (trueIndex === -1 || falseIndex === -1) return;
      // If TRUE currently comes after FALSE, swap them to hint ordering.
      if (trueIndex > falseIndex) {
        const tmp = reorderedNodes[trueIndex];
        reorderedNodes[trueIndex] = reorderedNodes[falseIndex];
        reorderedNodes[falseIndex] = tmp;
      }
    },
  );

  const elkNodes = reorderedNodes.map((node) => ({
    // Base node descriptor passed to ELK
    id: node.id,
    width: nodeWidth[node.type],
    height: nodeHeight[node.type],
    // For decision nodes we define explicit ports for true/false branching so ELK
    // maintains left->right ordering rather than swapping edge order during crossing minimization.
    ...(node.type === NODE_TYPES.DECISION
      ? {
          layoutOptions: {
            // Keep ports on their sides and preserve the explicit order in the 'ports' array.
            'elk.portConstraints': 'FIXED_ORDER',
          },
          ports: [
            {
              id: `${node.id}_true`,
              properties: { 'elk.port.side': 'SOUTH' }, // left (first) bottom port
            },
            {
              id: `${node.id}_false`,
              properties: { 'elk.port.side': 'SOUTH' }, // right (second) bottom port
            },
          ],
        }
      : {}),
  }));

  const elkEdges = validEdges.map((edge) => ({
    id: edge.id,
    sources: [edge.source],
    targets: [edge.target],
    // If the React Flow edge originated from a specific source handle, pass that through
    // so ELK routes from the corresponding port.
    ...(edge.sourceHandle ? { sourcePort: edge.sourceHandle } : {}),
  }));

  const graph = {
    id: 'root',
    layoutOptions,
    children: elkNodes,
    edges: elkEdges,
  };

  const layoutedGraph = await elk.layout(graph);

  const layoutedNodes = validNodes.map((node) => {
    const elkNode = layoutedGraph.children?.find((n) => n.id === node.id);
    return {
      ...node,
      targetPosition: Position.Top,
      sourcePosition: Position.Bottom,
      position: {
        x: elkNode?.x ?? 0,
        y: elkNode?.y ?? 0,
      },
    };
  });
  // Ports were added for decision nodes above; ELK now routes from those fixed-order ports.

  // --- Post-processing ordering enforcement -----------------------------------------------
  // Even with port + model order hints ELK can still place the TRUE branch target to the right
  // of the FALSE branch target if doing so reduces crossings deeper in the graph. For UX
  // consistency we enforce TRUE (left) / FALSE (right) locally when they end up inverted. We
  // only swap the sibling root nodes' x positions (not whole subtrees) to minimize disruption.
  // If future complex subtrees cause overlap, this can be expanded to shift entire reachable
  // subtrees instead of a simple swap.
  const nodeById: Record<string, LayoutNode> = Object.fromEntries(
    layoutedNodes.map((n) => [n.id, n]),
  );
  // Build quick lookup of edges by source handle
  const trueEdges = validEdges.filter((e) => e.sourceHandle?.endsWith('_true'));
  trueEdges.forEach((trueEdge) => {
    const { source } = trueEdge;
    /* Find the corresponding FALSE edge from the same decision node */
    const falseEdge = validEdges.find(
      (e) => e.source === source && e.sourceHandle?.endsWith('_false'),
    );
    if (!falseEdge) return; // No FALSE edge found - skip

    const trueNode = nodeById[trueEdge.target];
    const falseNode = nodeById[falseEdge.target];
    if (!trueNode || !falseNode) return; // Missing target node(s) - skip

    // If TRUE node is to the right of FALSE node, swap their x positions
    if (trueNode.position.x > falseNode.position.x) {
      const tmpX = trueNode.position.x;
      trueNode.position.x = falseNode.position.x;
      falseNode.position.x = tmpX;
    }
  });

  // --- Post-processing vertical normalization ---------------------------------------------
  // Guarantee TRUE and FALSE branch targets of a decision share a common vertical layer just
  // below the decision node with a minimum visual gap. We shift the *shallower* subtree down
  // rather than pulling the deeper one up, to avoid collisions with earlier layers.
  const MIN_DECISION_GAP = Number(
    layoutOptions['elk.layered.spacing.nodeNodeBetweenLayers'],
  );

  // Build quick adjacency for forward traversal, where the record
  // key is the source node id and the value is the list of target ids.
  const outgoingBySource: Record<string, string[]> = {};
  validEdges.forEach((e) => {
    outgoingBySource[e.source] = outgoingBySource[e.source] || [];
    outgoingBySource[e.source].push(e.target);
  });

  const decisionNodes = Object.values(nodeById).filter(
    (n) => n.type === NODE_TYPES.DECISION,
  );

  const getNodeHeight = (n: LayoutNode) => nodeHeight[n.type] ?? 0;

  // Handles moving an entire subtree, starting from rootId, by delta y.
  // Uses BFS to traverse all reachable nodes. Skips nodes with multiple parents
  // (shared convergence) to avoid distorting other paths.
  // delta can be negative (move up) or positive (move down)
  const shiftSubtree = (rootId: string, delta: number) => {
    if (delta === 0) return;
    const queue = [rootId];
    const visited = new Set(queue);
    while (queue.length) {
      const id = queue.shift();
      if (id) {
        const node = nodeById[id];
        if (node) node.position.y += delta; // delta can be negative (move up) or positive (move down)
        (outgoingBySource[id] || []).forEach((childId) => {
          // Skip if child has another parent (shared convergence) to avoid distorting other paths
          const multiParent = validEdges.some(
            (e) => e.target === childId && e.source !== id,
          );
          if (!multiParent && !visited.has(childId)) {
            visited.add(childId);
            queue.push(childId);
          }
        });
      }
    }
  };

  decisionNodes.forEach((decisionNode) => {
    const decisionNodeHeight = getNodeHeight(decisionNode);
    const decisionNodeBottom = decisionNode.position.y + decisionNodeHeight;
    // Identify true/false targets
    const trueEdge = validEdges.find(
      (e) => e.source === decisionNode.id && e.sourceHandle?.endsWith('_true'),
    );
    const falseEdge = validEdges.find(
      (e) => e.source === decisionNode.id && e.sourceHandle?.endsWith('_false'),
    );
    if (!trueEdge || !falseEdge) return;
    const trueNode = nodeById[trueEdge.target];
    const falseNode = nodeById[falseEdge.target];
    if (!trueNode || !falseNode) return;

    // Decide final unified Y: we prefer pulling the deeper one UP to the shallower level if the
    // shallower level still respects the minimum gap; otherwise we fallback to pushing both down.
    const shallowerY = Math.min(trueNode.position.y, falseNode.position.y);
    const minAllowedY = decisionNodeBottom + MIN_DECISION_GAP;
    const finalY = Math.max(shallowerY, minAllowedY);

    // Move TRUE subtree if its root y != finalY
    if (trueNode.position.y !== finalY) {
      shiftSubtree(trueNode.id, finalY - trueNode.position.y);
    }
    // Move FALSE subtree if its root y != finalY
    if (falseNode.position.y !== finalY) {
      shiftSubtree(falseNode.id, finalY - falseNode.position.y);
    }
  });

  return { nodes: Object.values(nodeById), edges: validEdges };
};
